import { getStoredToken } from '../auth'
import type { CreateRateRequest, Rate, RateAuditTrailEntry } from './rates.types'

const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

// Carries the HTTP status so callers can tell a 409 (rate already exists for that
// date and term) or a 403 (missing brokerPricingAdministrator) from a generic failure.
export class RateRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'RateRequestError'
    this.status = status
  }
}

const getAuthorizationHeader = () => {
  const token = getStoredToken()

  if (!token?.jwt) {
    throw new RateRequestError('Missing authentication token.', 401)
  }

  return { authorization: `bearer ${token.jwt}` }
}

const request = async <T>(path: string, method: 'GET' | 'POST' = 'GET', body?: unknown): Promise<T | undefined> => {
  if (!API_BASE_URL) {
    throw new Error('Missing API base URL (VITE_DAPI_URL).')
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    let message = 'Request failed'
    try {
      const data = (await response.json()) as { message?: string; error?: { message?: string } }
      if (data?.message) message = data.message
      if (data?.error?.message) message = `${message}: ${data.error.message}`
    } catch {
      // Keep fallback message.
    }
    throw new RateRequestError(message, response.status)
  }

  const text = await response.text()
  if (!text) return undefined

  return JSON.parse(text) as T
}

export const fetchRateHistory = async (): Promise<Rate[]> => {
  return (await request<Rate[]>('/borgo/distributor/rates/history')) ?? []
}

export const createRate = async (payload: CreateRateRequest): Promise<void> => {
  await request('/borgo/distributor/rates', 'POST', payload)
}

export const fetchRateAuditTrail = async (): Promise<RateAuditTrailEntry[]> => {
  return (await request<RateAuditTrailEntry[]>('/borgo/distributor/rates/audit-trail')) ?? []
}
